/**
 * What happens when you tap a phone number in a message.
 *
 * The number is looked up the moment the sheet opens, and what it offers
 * depends on the answer:
 *
 *   on ZIvapp      → Message them, which opens the conversation
 *   not on ZIvapp  → Invite them, which shares a link
 *
 * Always also: copy, and call. Looking up first rather than offering
 * "Message" and failing afterwards, because the whole point of the
 * feature is telling you which of the two situations you are in.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  contacts as contactService, chats as chatService,
  toE164, isValidE164, formatDisplay
} from '@zivapp/shared'
import { countryByCode } from '@zivapp/shared/countries'
import Avatar from '../ui/Avatar.jsx'
import './PhoneSheet.css'

const DEFAULT_DIAL = countryByCode('NG')?.dial ?? '234'

export default function PhoneSheet ({ raw, onClose }) {
  const navigate = useNavigate()

  const [state, setState] = useState('looking')   // looking | found | absent | invalid
  const [person, setPerson] = useState(null)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)

  // Normalised once: everything below works on the E.164 form, and the
  // raw text is only ever shown.
  const [e164] = useState(() => {
    try {
      const value = toE164(raw, DEFAULT_DIAL)
      return isValidE164(value) ? value : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (!e164) { setState('invalid'); return }

    let alive = true
    ;(async () => {
      try {
        const found = await contactService.findByPhone(e164)
        if (!alive) return
        setPerson(found)
        setState(found ? 'found' : 'absent')
      } catch {
        // A lookup that fails is not the same as nobody being there, so
        // it offers the invite rather than claiming they are missing.
        if (alive) setState('absent')
      }
    })()

    return () => { alive = false }
  }, [e164])

  async function message () {
    if (!person || busy) return
    setBusy(true)
    try {
      const chat = await chatService.getOrCreateDirect(person.id)
      onClose()
      navigate(`/chats/${chat.id}`)
    } finally {
      setBusy(false)
    }
  }

  async function copy () {
    try {
      await navigator.clipboard.writeText(e164 ?? raw)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  async function invite () {
    const text = `Message me on ZIvapp: ${window.location.origin}`
    // The native share sheet where there is one — on a phone that is how
    // you reach WhatsApp, SMS and email at once. The clipboard is the
    // fallback on a desktop browser, which has no share sheet.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ZIvapp', text })
        return
      } catch {
        // Dismissed, or refused. Fall through to copying.
      }
    }
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="phs" role="dialog" aria-modal="true" aria-label="Phone number">
      <button className="phs__scrim" type="button" onClick={onClose} aria-label="Close" />

      <div className="phs__sheet">
        <header className="phs__head">
          <Avatar name={person?.displayName || raw} url={person?.avatarUrl} size={54} />
          <div className="phs__ident">
            <span className="phs__name">
              {person?.displayName || formatDisplay(e164 ?? raw)}
            </span>
            <span className="phs__sub">
              {state === 'looking' && 'Checking ZIvapp…'}
              {state === 'found' && 'On ZIvapp'}
              {state === 'absent' && 'Not on ZIvapp yet'}
              {state === 'invalid' && 'Not a phone number we recognise'}
            </span>
          </div>
        </header>

        <div className="phs__actions">
          {state === 'found' && (
            <button
              type="button"
              className="phs__action phs__action--primary"
              onClick={message}
              disabled={busy}
              data-testid="phone-message"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 5h16a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 20 17H9l-5 4v-4a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 4 5Z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
              {busy ? 'Opening…' : `Message ${person?.displayName || 'them'} on ZIvapp`}
            </button>
          )}

          {state === 'absent' && (
            <button
              type="button"
              className="phs__action phs__action--primary"
              onClick={invite}
              data-testid="phone-invite"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="10" cy="8.4" r="3.5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3.4 19v-.8a5 5 0 0 1 5-5h3.2" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" />
                <path d="M18 13v6M15 16h6" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" />
              </svg>
              Invite to ZIvapp
            </button>
          )}

          {e164 && (
            <>
              <a className="phs__action" href={`tel:${e164}`}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                    stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                </svg>
                Call {formatDisplay(e164)}
              </a>

              <button type="button" className="phs__action" onClick={copy}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="9" y="9" width="11" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M15 6.5A1.5 1.5 0 0 0 13.5 5h-8A1.5 1.5 0 0 0 4 6.5v9A1.5 1.5 0 0 0 5.5 17"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                {copied ? 'Copied' : 'Copy number'}
              </button>

              {/* Only when they are not already a contact — saving
                  somebody you have saved is a row that does nothing. */}
              {state === 'absent' && (
                <button
                  type="button"
                  className="phs__action"
                  onClick={() => { onClose(); navigate('/contacts/new') }}
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="8.6" r="3.6" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M3.8 19.4v-.8a5 5 0 0 1 5-5h4.4" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M18 14v6M15 17h6" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" />
                  </svg>
                  Add to contacts
                </button>
              )}
            </>
          )}
        </div>

        <button type="button" className="phs__cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}
